import { Component, OnDestroy, AfterViewInit, OnInit } from '@angular/core';
import { BuildHealthService } from '../../services/build-health.service';
import { DateHelpers } from 'src/app/utilities/DateHelpers';

import * as c3 from 'c3';
import { Subscription } from 'rxjs';
import { ChartDefinitionService } from 'src/app/services/chart-definition.service';
import { CountdownService } from '../../services/countdown.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-build-widget',
  templateUrl: './build-widget.component.html',
  styleUrls: ['./build-widget.component.css']
})
export class BuildWidgetComponent implements OnInit, AfterViewInit, OnDestroy {

  private dateHelpers = new DateHelpers()

  private loading: boolean = true;
  private count: number = 60;
  private updateSubscription: Subscription;

  private buildChart: c3.Chart;
  private definitionChart: c3.Chart;

  constructor(
    private buildHealthService: BuildHealthService,
    private chartDefinitionService: ChartDefinitionService,
    private countdownService: CountdownService
  ) { }

  ngOnInit(): void {
    this.buildChart = this.chartDefinitionService.buildChart('#build-health', 'Builds', 200, 300);
    this.definitionChart = this.chartDefinitionService.buildChart('#definition-health', 'Definitions', 200, 300);
  }

  ngAfterViewInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) { this.updateSubscription.unsubscribe(); }
    if (this.buildChart && this.buildChart.destroy) { this.buildChart.destroy(); }
    if (this.definitionChart && this.definitionChart.destroy) { this.definitionChart.destroy(); }
    delete this.buildChart;
    delete this.definitionChart;
  }

  public load(): void {
    this.loading = true;
    this.loadData(50, this.dateHelpers.atBeginningOfDay(new Date()), this.dateHelpers.atEndOfDay(new Date()));
  }

  public reportButtonText(): string {
    if (this.loading) { return 'Loading...'; }
    if (this.count > 0) { return `Load Data - ${this.count}`; }
    return 'Load Data';
  }

  private loadData(numberOfBuilds: number, startDate: Date, endDate: Date): void {
    this.buildHealthService.getBuilds(numberOfBuilds, startDate, endDate)
    .source.pipe(finalize(() => {
      this.loading = false;
    }))
    .subscribe(response => {
      if (response && response.ok) {
        const data = response.body as any[];
        const justBuilds = data.map(d => d.buildHistory.filter(f => f.build.id > 0));

        const realBuilds = (justBuilds as any).flat().map(f=>f.build);
        const bldTotal = realBuilds.length;
        const bldSucceeded = realBuilds.filter(f=>f.result==='succeeded').length;
        const bldFailed = realBuilds.filter(f=>f.result==='failed').length;
        const bldInProgress = realBuilds.filter(f=>f.result===null).length;
        const bldOther = bldTotal - bldSucceeded - bldFailed - bldInProgress;

        const mostRecentBuilds = justBuilds.map(m=>m.reverse()[0]).map(m => m.build);
        const defSucceeded = mostRecentBuilds.filter(f=>f.result==='succeeded').length;
        const defFailed = mostRecentBuilds.filter(f=>f.result==='failed').length;
        const defInProgress = mostRecentBuilds.filter(f=>f.result===null).length;
        const defOther = data.length - defSucceeded - defFailed - defInProgress;

        this.buildChart.load({columns: [['data1', bldTotal], ['data2', bldFailed], ['data3', bldInProgress], ['data4', bldOther]]});
        this.buildChart.data.names({data1: `Succeeded (${bldTotal})`, data2: `Failed (${bldFailed})`, data3: `In progress (${bldInProgress})`, data4: `Other (${bldOther})`});

        this.definitionChart.load({columns: [['data1', data.length], ['data2', defFailed], ['data3', defInProgress], ['data4', defOther]]});
        this.definitionChart.data.names({data1: `Succeeded (${data.length})`, data2: `Failed (${defFailed})`, data3: `In progress (${defInProgress})`, data4: `Other (${defOther})`});
        if (this.updateSubscription) { this.updateSubscription.unsubscribe(); }
        this.updateSubscription =  this.countdownService.start('build-health-widget-update', 60).subscribe(counter => {
          this.count = counter;
          if (counter === 0) { this.load(); }
        });
            
        this.loading = false;
      }
    })
  }
}